// Schedules tab — every delivery schedule you own (super admins can see everyone's).
import { useEffect, useState } from "react";
import { CalendarClock, Loader2, Pencil, Play, Trash2, AlertTriangle, CheckCircle2, PauseCircle, Clock, UserCog, UserX, HardDriveUpload } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { ScheduleDialog, SCHEDULE_META_KEYS } from "./ScheduleDialog";
import { ScheduleHealthBanner } from "./ScheduleHealthBanner";
import { ReassignOwnerDialog } from "./ReassignOwnerDialog";
import { describeParams, formatLabel } from "./reportsApi";

function StatusPill({ s }) {
  if (!s.enabled) return <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600" data-testid="schedule-status-paused"><PauseCircle className="h-3 w-3" /> Paused</span>;
  if (s.last_status === "failed") return <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-700" data-testid="schedule-status-failed" title={s.last_error}><AlertTriangle className="h-3 w-3" /> Last run failed</span>;
  if (s.last_status) return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700" data-testid="schedule-status-ok"><CheckCircle2 className="h-3 w-3" /> {s.last_status === "empty" ? "Last run: no rows" : `Last run: ${s.last_status}`}</span>;
  return <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-700" data-testid="schedule-status-new"><Clock className="h-3 w-3" /> Not run yet</span>;
}

export function SchedulesTab({ reports, refreshKey, onChanged }) {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [all, setAll] = useState(false);
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(null);
  const [handoff, setHandoff] = useState(null);

  const load = () => api.get("/report-hub/schedules", { params: { all } })
    .then((r) => setRows(r.data.schedules || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, [all, refreshKey]);     // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (s, enabled) => {
    try {
      const body = { ...s, enabled };
      SCHEDULE_META_KEYS.forEach((k) => delete body[k]);
      await api.put(`/report-hub/schedules/${s.schedule_id}`, body);
      toast.success(enabled ? "Schedule resumed" : "Schedule paused");
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update"); }
  };

  const runNow = async (s) => {
    setBusy(s.schedule_id);
    try {
      const { data } = await api.post(`/report-hub/schedules/${s.schedule_id}/run`);
      if (s.channel === "sftp") toast.success(data.status === "empty" ? "No rows — upload skipped" : `Uploaded to ${data.destination} — ${data.remote_path}`);
      else toast.success(data.status === "paused" ? `Generated (${data.rows} rows) — outbound email is paused` : `Sent (${data.rows} rows)`);
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send"); load(); }
    finally { setBusy(""); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete the schedule "${s.name}"?`)) return;
    try { await api.delete(`/report-hub/schedules/${s.schedule_id}`); toast.success("Schedule deleted"); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete"); }
  };

  const reportOf = (s) => reports.find((r) => r.key === s.report_key);

  return (
    <div className="space-y-3" data-testid="schedules-tab">
      <ScheduleHealthBanner refreshKey={`${refreshKey}-${rows?.length ?? 0}-${busy}`} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-500">Each schedule runs in its owner's data scope and emails the chosen recipients — or uploads the file to an SFTP destination.</p>
        {isSuper && (
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <Switch checked={all} onCheckedChange={setAll} data-testid="schedules-show-all" /> Show everyone's schedules
          </label>
        )}
      </div>
      {rows === null ? (
        <div className="py-10 text-center text-sm text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <Card className="border-dashed border-slate-300 p-10 text-center dark:border-slate-700 dark:bg-slate-900" data-testid="schedules-empty">
          <CalendarClock className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">No schedules yet. Open a report in the Catalog and press <b>Schedule</b>.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {rows.map((s) => {
            const rep = reportOf(s);
            return (
              <Card key={s.schedule_id} className="flex flex-wrap items-center gap-3 border-slate-200 px-4 py-3 shadow-sm dark:border-slate-700 dark:bg-slate-900" data-testid={`schedule-row-${s.schedule_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate font-heading text-sm font-semibold text-slate-900 dark:text-slate-100" data-testid="schedule-row-name">{s.name}</span>
                    <StatusPill s={s} />
                    {!s.mine && <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-700" data-testid="schedule-owner-pill">Owner: {s.owner_name}</span>}
                    {s.owner_active === false && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-700" data-testid="schedule-owner-inactive">
                        <UserX className="h-3 w-3" /> Owner inactive — hand off
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    {s.report_label} · {formatLabel(s.format, s.channel)} · {s.schedule_label}
                    {rep && describeParams(rep, s.params).length ? ` · ${describeParams(rep, s.params).join(" · ")}` : ""}
                  </div>
                  <div className="mt-0.5 text-[11px] text-slate-400">
                    {s.channel === "sftp"
                      ? <span className="inline-flex items-center gap-1" data-testid="schedule-row-destination"><HardDriveUpload className="h-3 w-3" /> SFTP → {s.destination_name || "destination removed"}</span>
                      : <>To {[...(s.recipients || []), ...(s.send_to_self ? [`${s.owner_name} (owner)`] : [])].join(", ") || "—"}</>}
                    {s.next_run_at && s.enabled ? ` · next ${new Date(s.next_run_at).toLocaleString()}` : ""}
                    {s.last_run_at ? ` · last ${new Date(s.last_run_at).toLocaleString()}` : ""}
                  </div>
                </div>
                <Switch checked={s.enabled !== false} onCheckedChange={(v) => toggle(s, v)} data-testid={`schedule-toggle-${s.schedule_id}`} />
                <Button variant="ghost" size="icon" className="h-8 w-8" title="Send now" disabled={busy === s.schedule_id}
                        onClick={() => runNow(s)} data-testid={`schedule-run-${s.schedule_id}`}>
                  {busy === s.schedule_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit" disabled={!rep}
                        onClick={() => setEditing(s)} data-testid={`schedule-edit-${s.schedule_id}`}><Pencil className="h-4 w-4" /></Button>
                {isSuper && (
                  <Button variant="ghost" size="icon" className="h-8 w-8" title="Hand off to another owner"
                          onClick={() => setHandoff(s)} data-testid={`schedule-reassign-${s.schedule_id}`}><UserCog className="h-4 w-4" /></Button>
                )}
                <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-rose-600" title="Delete"
                        onClick={() => remove(s)} data-testid={`schedule-delete-${s.schedule_id}`}><Trash2 className="h-4 w-4" /></Button>
              </Card>
            );
          })}
        </div>
      )}
      {editing && reportOf(editing) && (
        <ScheduleDialog report={reportOf(editing)} schedule={editing} open onClose={(saved) => { setEditing(null); if (saved) { load(); onChanged?.(); } }} />
      )}
      {handoff && (
        <ReassignOwnerDialog schedule={handoff} open onClose={(saved) => { setHandoff(null); if (saved) { load(); onChanged?.(); } }} />
      )}
    </div>
  );
}
